import {Component, computed, effect, inject, input, OnDestroy, output, signal} from '@angular/core';
import {AudioService} from '../../../../../services/audio/audio.service';

@Component({
  selector: 'app-player-controls',
  standalone: true,
  imports: [],
  templateUrl: './player-controls.component.html',
  styleUrl: './player-controls.component.scss'
})
export class PlayerControlsComponent implements OnDestroy {
  // Inputs for parent component data
  currentAudioIndex = input.required<number>();
  audioResults = input.required<any[]>();
  isLoadingAlternatives = input<boolean>(false);
  // Outputs for parent component actions
  playPrevious = output<void>();
  playNext = output<void>();
  togglePlayPause = output<void>();
  volumeUpdated = output<number>();
  // Computed properties for UI state
  readonly canPlayPrevious = computed(() => this.currentAudioIndex() > 0);
  readonly canPlayNext = computed(() => this.currentAudioIndex() < this.audioResults().length - 1);
  readonly trackCounterText = computed(() =>
    `Option #${this.currentAudioIndex() + 1} (of ${this.audioResults().length})`
  );
  // Injected services
  private audioService = inject(AudioService);
  // Audio service proxy properties for template access
  readonly isPlaying = computed(() => this.audioService.isPlaying());
  readonly isLoading = computed(() => this.audioService.isLoading());
  readonly volume = computed(() => this.audioService.volume());
  readonly currentTime = computed(() => this.audioService.currentTime());
  readonly duration = computed(() => this.audioService.duration());
  readonly startTime = computed(() => this.audioService.startTime());
  readonly endTime = computed(() => this.audioService.endTime());
  readonly isAutoplayBlocked = computed(() => this.audioService.isAutoplayBlocked());
  // Internal state
  private volumeSliderElement = signal<HTMLInputElement | null>(null);

  // Local volume state for immediate UI updates
  private localVolume = signal<number | null>(null);
  // Computed volume that prioritizes local state for immediate feedback
  readonly displayVolume = computed(() => {
    const local = this.localVolume();
    return local !== null ? local : this.volume();
  });
  private isUpdatingVolume = signal<boolean>(false);
  private syncCheckInterval: any = null;

  constructor() {
    // Effect to sync volume slider when audio service volume changes externally
    effect(() => {
      const serviceVolume = this.volume();
      const localVolume = this.localVolume();
      const isUpdating = this.isUpdatingVolume();

      // Only sync if we're not currently updating the volume locally
      if (!isUpdating && localVolume === null) {
        this.syncVolumeSlider(serviceVolume);
      }
    });

    // Effect to update volume slider fill when display volume changes
    effect(() => {
      const volume = this.displayVolume();
      this.updateVolumeSliderFill(volume);
    });

    // Start backup sync mechanism
    this.startBackupSyncCheck();
  }

  ngOnDestroy(): void {
    // Clean up the sync check interval
    if (this.syncCheckInterval) {
      clearInterval(this.syncCheckInterval);
      this.syncCheckInterval = null;
    }
  }

  /**
   * Handle previous track button click
   */
  onPlayPrevious(): void {
    if (this.canPlayPrevious()) {
      this.playPrevious.emit();
    }
  }

  /**
   * Handle next track button click
   */
  onPlayNext(): void {
    if (this.canPlayNext()) {
      this.playNext.emit();
    }
  }

  /**
   * Handle play/pause button click
   */
  onTogglePlayPause(): void {
    this.togglePlayPause.emit();
  }

  /**
   * Update the volume and apply it to the audio element
   * @param event Change event from volume slider
   */
  updateVolume(event: Event): void {
    const input = event.target as HTMLInputElement;
    const volume = parseFloat(input.value);

    // Set updating flag to prevent sync conflicts
    this.isUpdatingVolume.set(true);

    // Update local state immediately for responsive UI
    this.localVolume.set(volume);

    // Store slider reference for CSS custom property updates
    this.volumeSliderElement.set(input);

    // Update the volume slider fill with CSS custom property
    this.updateVolumeSliderFill(volume);

    // Emit volume change to parent
    this.volumeUpdated.emit(volume);
  }

  /**
   * Handle volume slider mouse/touch end events to sync with audio service
   */
  onVolumeSliderEnd(): void {
    // Clear local state to sync back with audio service
    // Increased delay to account for audio service debounce (50ms) + buffer
    setTimeout(() => {
      this.localVolume.set(null);
      this.isUpdatingVolume.set(false);

      // Force sync with current service volume
      const serviceVolume = this.volume();
      this.syncVolumeSlider(serviceVolume);
    }, 150); // Increased from 100ms to 150ms
  }

  /**
   * Handle programmatic volume updates (not from user interaction)
   * This can be called by parent components to ensure slider stays in sync
   */
  syncVolumeFromService(): void {
    const serviceVolume = this.volume();
    this.syncVolumeSlider(serviceVolume);
  }

  /**
   * Returns the volume as a formatted percentage string
   */
  volumeDisplay(): string {
    return Math.round(this.displayVolume() * 100) + '';
  }

  /**
   * Format time in minutes:seconds format
   * @param seconds Time in seconds
   */
  formatTime(seconds: number): string {
    if (isNaN(seconds) || seconds < 0) {
      return '00:00';
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
   * Calculate the current playback progress as a percentage
   */
  calculateProgressPercentage(): number {
    const currentTime = this.currentTime();
    const startTime = this.startTime();
    const endTime = this.endTime();
    const duration = this.duration();

    // If no duration or times are available, return 0
    if (!duration || duration <= 0) {
      return 0;
    }

    // Handle section playback (start and end times defined)
    if (startTime >= 0 && endTime && endTime > 0) {
      const sectionStart = startTime;
      const sectionEnd = Math.min(endTime, duration);
      const sectionDuration = sectionEnd - sectionStart;

      if (sectionDuration <= 0) {
        return 0;
      }

      // Calculate relative position within the section
      const relativePosition = Math.max(0, currentTime - sectionStart);
      return Math.min(100, (relativePosition / sectionDuration) * 100);
    }

    // Handle start-time-only playback
    if (startTime > 0 && (!endTime || endTime <= 0 || endTime >= duration)) {
      const remainingDuration = duration - startTime;
      if (remainingDuration <= 0) {
        return 100;
      }

      const relativePosition = Math.max(0, currentTime - startTime);
      return Math.min(100, (relativePosition / remainingDuration) * 100);
    }

    // Default: full track playback
    return Math.min(100, (currentTime / duration) * 100);
  }

  /**
   * Calculate the current time relative to the playback section
   */
  calculateCurrentRelativeTime(): number {
    const currentTime = this.currentTime();
    const startTime = this.startTime();
    const endTime = this.endTime();
    const duration = this.duration();

    // If no valid times, return current time
    if (!duration || duration <= 0) {
      return currentTime;
    }

    // If we have start time, calculate relative to start
    if (startTime >= 0) {
      const relativeTime = Math.max(0, currentTime - startTime);

      // If we also have end time, ensure we don't exceed the section duration
      if (endTime && endTime > 0) {
        const sectionDuration = endTime - startTime;
        return Math.min(relativeTime, sectionDuration);
      }

      return relativeTime;
    }

    // Default: return current time as-is
    return currentTime;
  }

  /**
   * Format the section duration for display
   */
  formatSectionDuration(): string {
    const startTime = this.startTime();
    const endTime = this.endTime();
    const duration = this.duration();

    // If we have valid start and end times, calculate section duration
    if (startTime >= 0 && endTime && endTime > 0) {
      const sectionDuration = endTime - startTime;
      return this.formatTime(Math.max(0, sectionDuration));
    }

    // If we only have start time but no end time, calculate from start to total duration
    if (startTime > 0 && (!endTime || endTime <= 0 || endTime >= duration)) {
      const remainingDuration = duration - startTime;
      return this.formatTime(Math.max(0, remainingDuration));
    }

    // Default: show total duration
    return this.formatTime(duration);
  }

  /**
   * Start a backup sync check that runs every few seconds to catch any missed sync issues
   */
  private startBackupSyncCheck(): void {
    this.syncCheckInterval = setInterval(() => {
      // Only run backup sync if we're not currently updating volume
      if (!this.isUpdatingVolume() && this.localVolume() === null) {
        const slider = this.volumeSliderElement() || document.querySelector('.volume-slider') as HTMLInputElement;
        const serviceVolume = this.volume();

        if (slider && serviceVolume !== undefined) {
          const sliderValue = parseFloat(slider.value);

          // Check if there's a significant difference between slider and service
          if (Math.abs(sliderValue - serviceVolume) > 0.05) { // 5% tolerance
            console.warn(`Volume bar sync issue detected and corrected - slider: ${sliderValue}, service: ${serviceVolume}`);
            this.syncVolumeSlider(serviceVolume);
          }
        }
      }
    }, 3000); // Check every 3 seconds
  }

  /**
   * Sync the volume slider with a specific volume value
   * @param volume Volume value to sync to
   */
  private syncVolumeSlider(volume: number): void {
    const slider = this.volumeSliderElement() || document.querySelector('.volume-slider') as HTMLInputElement;

    if (slider) {
      // Check if the current value is different to avoid unnecessary updates
      const currentValue = parseFloat(slider.value);
      if (Math.abs(currentValue - volume) > 0.01) { // Small tolerance for floating point comparison
        // Force update the slider DOM value
        slider.value = volume.toString();

        // Update the visual fill
        this.updateVolumeSliderFill(volume);

        // Store the slider reference if we found it
        if (!this.volumeSliderElement()) {
          this.volumeSliderElement.set(slider);
        }
      }
    }
  }

  /**
   * Update the volume slider fill using CSS custom property
   * @param volume Volume level (0-1)
   */
  private updateVolumeSliderFill(volume: number): void {
    // Clamp volume to valid range
    const clampedVolume = Math.max(0, Math.min(1, volume));
    const percentageValue = `${clampedVolume * 100}%`;

    // Try to get slider from stored reference first
    let slider = this.volumeSliderElement();

    // If no stored reference, try to find it in the DOM
    if (!slider) {
      slider = document.querySelector('.volume-slider') as HTMLInputElement;
      if (slider) {
        this.volumeSliderElement.set(slider);
      }
    }

    if (slider) {
      try {
        slider.style.setProperty('--volume-percent', percentageValue);
      } catch (error) {
        console.warn('Failed to update volume slider fill:', error);
      }
    } else {
      // If we still can't find the slider, try again on next frame
      // This can happen during initial render
      requestAnimationFrame(() => {
        const retrySlider = document.querySelector('.volume-slider') as HTMLInputElement;
        if (retrySlider) {
          this.volumeSliderElement.set(retrySlider);
          try {
            retrySlider.style.setProperty('--volume-percent', percentageValue);
          } catch (error) {
            console.warn('Failed to update volume slider fill on retry:', error);
          }
        }
      });
    }
  }
}
